import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Bell, BookOpen, Check, Loader2, Plus, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, IconChip } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { LogoMark } from "@/components/logo";
import {
  LiteraturePicker,
  type PreviewTerm,
} from "@/components/recovery/literature-picker";
import {
  ApiError,
  updateProfile,
  updateReminders,
  type FellowshipId,
} from "@/lib/api";

/**
 * Gentle first-run setup. Every step is skippable — nothing here is required
 * to use the app, and everything can be changed later in Settings. One
 * decision per screen (§9). Finishing marks profiles.onboarding_completed so
 * Home stops redirecting here.
 */

type Step = "welcome" | "literature" | "sobriety" | "reminder" | "done";
const ORDER: Step[] = ["welcome", "literature", "sobriety", "reminder", "done"];

const browserTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
};

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("welcome");
  const [name, setName] = useState("");
  const [sobriety, setSobriety] = useState("");
  // Which literature the readings should cite (Fellowships PRD §4). Optional
  // and skippable like everything here; nothing is selected by default, and
  // that stays a first-class answer.
  const [fellowships, setFellowships] = useState<FellowshipId[]>([]);
  const [term, setTerm] = useState<PreviewTerm>({
    adj: "in recovery",
    card: "Recovery Time",
    date: "Recovery start date",
    neutral: true,
  });
  const [remindOn, setRemindOn] = useState(false);
  const [remTime, setRemTime] = useState("20:00");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const idx = ORDER.indexOf(step);

  async function finish() {
    setSaving(true);
    setError(null);
    try {
      await updateProfile({
        display_name: name.trim(),
        sobriety_start_date: sobriety || null,
        fellowships,
        onboarding_completed: true,
      });
      if (remindOn) {
        await updateReminders({
          reminder_time: remTime,
          timezone: browserTz(),
          is_active: true,
        }).catch(() => {}); // reminder is a nice-to-have; never blocks setup
      }
      setStep("done");
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) navigate("/signin");
      else setError("Couldn't save your setup. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col px-6 pb-10">
      <div className="pt-6">
        <Progress value={Math.round(((idx + 1) / ORDER.length) * 100)} />
      </div>

      {/* ------------------------------ welcome ------------------------------ */}
      {step === "welcome" && (
        <div className="flex flex-1 animate-fade-rise flex-col justify-center gap-5">
          <LogoMark className="h-14 w-14" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Welcome to Happy24</h1>
            <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
              A calm, private space for one honest check-in a day. No scores to beat,
              nothing to lose — just staying aware for the next 24 hours.
            </p>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="ob-name">
              What should we call you?
            </label>
            <Input
              id="ob-name"
              placeholder="A first name is plenty"
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Just for your greeting. Leave it blank if you'd rather.
            </p>
          </div>
          <Button size="xl" fullWidth onClick={() => setStep("literature")}>
            Continue
            <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Button>
        </div>
      )}

      {/* ----------------------------- literature ----------------------------- */}
      {step === "literature" && (
        <div className="flex flex-1 animate-fade-rise flex-col justify-center gap-5">
          <IconChip icon={BookOpen} tint="primary" />
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Optional
            </p>
            <h1 className="mt-1 text-xl font-semibold text-foreground">
              Which literature do you use?
            </h1>
            <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
              Only so the step readings point at the right book. Pick as many as apply, or
              none — you can change this anytime in Settings.
            </p>
          </div>
          <LiteraturePicker
            value={fellowships}
            onChange={(next, t) => {
              setFellowships(next);
              setTerm(t);
            }}
          />
          <div className="flex flex-col gap-2">
            <Button size="xl" fullWidth onClick={() => setStep("sobriety")}>
              Continue
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Button>
            <Button
              variant="ghost"
              fullWidth
              onClick={() => {
                setFellowships([]);
                setStep("sobriety");
              }}
            >
              Skip
            </Button>
          </div>
        </div>
      )}

      {/* ------------------------------ sobriety ------------------------------ */}
      {step === "sobriety" && (
        <div className="flex flex-1 animate-fade-rise flex-col justify-center gap-5">
          <IconChip icon={Shield} tint="primary" />
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              {term.neutral ? "Do you count time?" : `Do you have a ${term.date.toLowerCase()}?`}
            </h1>
            <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
              It's shown quietly on your dashboard — a fact, not a scoreboard. Skip
              this if you'd rather; you can add it anytime in Settings. The word here
              follows the literature you picked.
            </p>
          </div>
          <Input
            type="date"
            aria-label={term.date}
            max={new Date().toISOString().slice(0, 10)}
            value={sobriety}
            onChange={(e) => setSobriety(e.target.value)}
          />
          <div className="flex flex-col gap-2">
            <Button size="xl" fullWidth onClick={() => setStep("reminder")}>
              Continue
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Button>
            <Button
              variant="link"
              fullWidth
              className="text-[13px] text-muted-foreground"
              onClick={() => {
                setSobriety("");
                setStep("reminder");
              }}
            >
              Skip for now
            </Button>
          </div>
        </div>
      )}

      {/* ------------------------------ reminder ------------------------------ */}
      {step === "reminder" && (
        <div className="flex flex-1 animate-fade-rise flex-col justify-center gap-5">
          <IconChip icon={Bell} tint="primary" />
          <div>
            <h1 className="text-xl font-semibold text-foreground">Want a daily nudge?</h1>
            <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
              One gentle email a day, at a time you choose — only if you haven't
              already checked in. Never nagging.
            </p>
          </div>
          <Card>
            <CardContent className="flex items-center justify-between gap-3 px-4 py-3">
              <p className="text-sm font-medium text-foreground">Remind me to check in</p>
              <button
                type="button"
                role="switch"
                aria-checked={remindOn}
                aria-label="Toggle daily reminder"
                onClick={() => setRemindOn((v) => !v)}
                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                  remindOn ? "bg-primary" : "bg-border"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                    remindOn ? "translate-x-[22px]" : "translate-x-0.5"
                  }`}
                />
              </button>
            </CardContent>
          </Card>
          {remindOn && (
            <Input
              type="time"
              aria-label="Reminder time"
              value={remTime}
              onChange={(e) => setRemTime(e.target.value)}
            />
          )}
          {error && <p className="text-sm text-status-red-text">{error}</p>}
          <Button size="xl" fullWidth disabled={saving} onClick={finish}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
            ) : (
              <>
                Finish setup
                <Check className="h-4 w-4" strokeWidth={2} />
              </>
            )}
          </Button>
        </div>
      )}

      {/* -------------------------------- done -------------------------------- */}
      {step === "done" && (
        <div className="flex flex-1 animate-fade-rise flex-col items-center justify-center gap-5 text-center">
          <div className="flex h-16 w-16 animate-scale-in items-center justify-center rounded-full bg-primary/10 text-primary">
            <Check className="h-8 w-8" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              You're set{name.trim() ? `, ${name.trim()}` : ""}
            </h1>
            <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
              Everything here is private unless you choose to share it. One day at a
              time.
            </p>
          </div>
          <div className="flex w-full flex-col gap-3">
            <Button size="xl" fullWidth onClick={() => navigate("/checkin")}>
              <Plus className="h-4 w-4" strokeWidth={2} />
              Start your first check-in
            </Button>
            <Button variant="ghost" fullWidth onClick={() => navigate("/app")}>
              Go to my dashboard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
